import { Link } from "react-router-dom";
import { useWishlist } from "@/hooks/useWishlist";
import { useCart } from "@/hooks/useCart";
import { useProducts } from "@/hooks/useProducts";
import type { Product } from "@/lib/api";
import WishlistCard from "@/pages/wishlist/components/WishlistCard";
import WishlistEmpty from "@/pages/wishlist/components/WishlistEmpty";

export default function WishlistPage() {
  const { wishlistIds, clearWishlist, count } = useWishlist();
  const { addItem } = useCart();
  const { products } = useProducts();

  const wishlisted: Product[] = products.filter((p) =>
    wishlistIds.includes(p.id)
  );

  const handleAddAll = () => {
    wishlisted.forEach((product) => {
      addItem({
        productId: product.id,
        name: product.name,
        price: product.price,
        size: product.sizes[0],
        color: product.colors[0].name,
        colorHex: product.colors[0].hex,
        quantity: 1,
        image: product.images[0],
      });
    });
  };

  return (
    <div
      className="min-h-screen bg-moonlight pt-28 pb-24"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12 pb-8 border-b border-pearl/40">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-midnight/40 mb-2">
              Your Collection
            </p>
            <h1
              className="text-4xl md:text-5xl font-light text-midnight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Wishlist
            </h1>
            {count > 0 && (
              <p className="text-sm text-midnight/50 mt-2">
                {count} {count === 1 ? "item" : "items"} saved
              </p>
            )}
          </div>
          {count > 0 && (
            <div className="flex items-center gap-4">
              <button
                onClick={handleAddAll}
                className="text-[11px] tracking-widest uppercase bg-midnight text-moonlight px-6 py-3 hover:bg-midnight/85 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Add All to Cart
              </button>
              <button
                onClick={clearWishlist}
                className="text-[11px] tracking-widest uppercase text-midnight/40 hover:text-midnight border border-pearl/50 px-5 py-3 hover:border-midnight/40 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {count === 0 ? (
          <WishlistEmpty />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {wishlisted.map((product) => (
              <WishlistCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Continue Shopping */}
        {count > 0 && (
          <div className="mt-16 pt-8 border-t border-pearl/40 text-center">
            <p className="text-sm text-midnight/50 mb-4">
              Looking for more inspiration?
            </p>
            <Link
              to="/collections/new-arrivals"
              className="inline-block text-[11px] tracking-widest uppercase border border-midnight text-midnight px-8 py-3 hover:bg-midnight hover:text-moonlight transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
